import * as React from 'react'
import { signOut } from '../../utils/auth'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import { EyeClose, EyeOpen } from '../icons/main-menu'
import {
  IServiceObject,
  ITripFilters,
  IZoneObject,
  IGoinZoneObject,
  IServiceListFilterDTO,
  ITotalTripsPerServiceResponseDTO,
  TempVehicleCountByServiceResponseDTO,
  TempVehicleCountByServiceDTO
} from '../../types'
import Menu, { Divider, SubMenu, Item as MenuItem } from 'rc-menu'
import { canAccessByAuthToken, checkIfAdmin } from '../../utils/authorization'
import { IOrganizationTeamMemberJwtDTO, IProviderTeamMemberJwtDTO } from '../../types'
import CloseLeft from '../icons/main-menu/CloseLeft'
import Trip from '../icons/main-menu/Trip'
import Path from '../icons/main-menu/Path'
import ImageView from '../ui/ImageView'
import 'rc-menu/assets/index.css'
import ZonesListItem from './ZonesListItem'
import Grayarrow from '../icons/main-menu/Grayarrow'
import moment from 'moment'
import ExclamationCircle from '../icons/ui/ExclamationCircle'
import { TripStatuses } from 'src/constants/trip-statuses'
import history from 'src/history'
import PreloaderCircle from '../ui/PreloaderCircle'
import { Checkbox } from '@mobiscroll/react-latest'

interface IMenuTitleProps {
  orgId: string
  orgName: string
  userName: string
  isAdmin: boolean
}

const MenuTitle = (props: IMenuTitleProps) => {
  return (
    <div className='flex items-center cursor--pointer'>
      {props.isAdmin && <ImageView width='30px' height='30px' entityId={props.orgId} />}
      <div className='ml2 text-wrapper profile-title'>
        <div className='name-admin'>
          <h3 className='p0 m0'>
            {' '}
            {props.orgName}
            {props.isAdmin ? (
              <span className='grayarrow'>
                <Grayarrow />
              </span>
            ) : (
              <span className='greenarrow'>
                <Path />
              </span>
            )}
          </h3>
          {!props.isAdmin && (
            <span className='close-left'>
              <CloseLeft />
            </span>
          )}
        </div>
        {props.isAdmin && <p className='p0 m0 font-small'> {props.userName}</p>}
      </div>
    </div>
  )
}

interface IMainMenuProps {
  orgId: string
  orgName: string
  userName: string
  isAdmin: boolean
  orgRoles: IOrganizationTeamMemberJwtDTO[]
  providerRoles: IProviderTeamMemberJwtDTO[]
  services: IServiceObject[]
  tripsPerService: ITotalTripsPerServiceResponseDTO[]
  location: any
  fetchServices: (orgId: string) => Promise<any>
  fetchTotalTripsPerService: (orgId: string, dto: IServiceListFilterDTO) => Promise<any[]>
  updateTripFilters: (filters: ITripFilters) => Promise<any>
  tripFilters: ITripFilters
  resourcePermissions: any
  zones: { [key: string]: IZoneObject } | {}
  toggleZone?: (zone: { [key: string]: IZoneObject } | {}) => Promise<any>
  visible: boolean
  setVisible: (visible: boolean) => Promise<any>
  getGoinzones?: (orgId: string, serviceId: string) => Promise<any>
  fetchService: (serviceId: string) => void
  tempVehicleByService: TempVehicleCountByServiceDTO[]
  getTempVehicleByServiceCount: () => Promise<TempVehicleCountByServiceResponseDTO>
}

interface IMainMenuState {
  requestZoneVisible: boolean
  isFetching: boolean
  serviceIds: string[]
}

class MainMenu extends React.Component<IMainMenuProps, IMainMenuState> {
  private linkRef: any

  private newTripStatusIds = '1, 10, 19, 21, 25, 27, 29' // REQUESTED, CANCELLED, NO_RESPONSE_OPERATOR, SCHEDULE_REQUESTED, WAITING, DISPATCH_ASSIGNED_DRIVER, PENDING

  constructor(props: IMainMenuProps) {
    super(props)
    this.state = {
      requestZoneVisible: true,
      isFetching: true,
      serviceIds: []
    }
  }

  public async componentDidMount() {
    const { orgId } = this.props

    if (this.props.orgId) {
      await this.props.fetchServices(orgId)
      await this.props.getTempVehicleByServiceCount()
      await this.displayTotalTripsPerService(orgId, this.newTripStatusIds)
    }

    this.setState({ isFetching: false })
  }

  public componentDidUpdate(prevProps: IMainMenuProps) {
    const { orgId, services } = this.props

    if (prevProps.services.length !== services.length) {
      if (services.length) {
        const serviceIds = services.map(service => service.id)
        this.setState({ serviceIds })
        this.props.updateTripFilters({ serviceIds })
      } else {
        this.setState({ serviceIds: [] })
      }
    }

    if (prevProps.orgId !== orgId) {
      this.props.fetchServices(orgId)
      this.displayTotalTripsPerService(orgId, this.newTripStatusIds)
      this.props.getTempVehicleByServiceCount()
    }

    if (this.props.tripFilters.serviceId !== prevProps.tripFilters.serviceId && !this.props.tripFilters.serviceId) {
      this.setState({ requestZoneVisible: true })
      if (this.props.toggleZone) {
        this.props.toggleZone(this.setAllZonesVisiblity(this.props.zones, true))
      }

      this.displayTotalTripsPerService(orgId, this.newTripStatusIds)
      this.props.getTempVehicleByServiceCount()
    }
    // if user navigate to other route aside from trips route the serviceId will be removed in trips.tripFilters state
    if (this.shouldResetServiceIdFilter()) {
      if (this.props.tripFilters.serviceId) {
        this.props.updateTripFilters({ serviceId: undefined })
      }
    }

    // fetch total trips per service everytime the user is updating trip filters
    // (e.g in timeline view such as Day, Week, Month)
    if (
      this.props.tripFilters.timeStart !== prevProps.tripFilters.timeStart ||
      this.props.tripFilters.timeEnd !== prevProps.tripFilters.timeEnd
    ) {
      this.displayTotalTripsPerService(orgId, this.newTripStatusIds)
    }
  }

  public fetchGoinZones() {
    if (this.props.toggleZone) {
      this.props.toggleZone({})
    }
    if (
      this.props.getGoinzones &&
      this.props.tripFilters &&
      this.props.tripFilters.orgId &&
      this.props.tripFilters.serviceId
    ) {
      this.props.getGoinzones(
        this.props.tripFilters && this.props.tripFilters.orgId ? this.props.tripFilters.orgId : '',
        this.props.tripFilters && this.props.tripFilters.serviceId ? this.props.tripFilters.serviceId : ''
      )
    }
  }
  public shouldResetServiceIdFilter() {
    const allowedRoutes = ['/trips', '/scheduler', '/trip-feedbacks']
    const route = allowedRoutes.find(r => this.props.location.pathname.startsWith(r))
    return !route || this.props.location.pathname.startsWith('/trips-')
  }

  public displayTotalTripsPerService = async (orgId: string, filterStatusIds: string) => {
    const { timeStart, timeEnd } = this.props.tripFilters
    const from = timeStart ? moment(timeStart) : moment()
    const to = timeEnd ? moment(timeEnd) : moment()
    // Get the total trips from 1 hour ago up to the current time plus 2 hours (REQUESTED, SCHEDULE_REQUEST last trip status)
    if (orgId) {
      await this.props.fetchTotalTripsPerService(orgId, {
        fromDate: from
          // .subtract(1, 'hours')
          .startOf('day')
          .toISOString(),
        toDate: to
          // .add(2, 'hours')
          .startOf('day')
          .endOf('day')
          .toISOString(),
        lastStatusIds: filterStatusIds
      })
    }
  }

  public toggleVisible = () => {
    const status = !this.props.visible
    this.props.setVisible(status)
    if (this.props.toggleZone) {
      this.props.toggleZone(this.setAllZonesVisiblity(this.props.zones, status))
    }
  }

  public setAllZonesVisiblity = (zones: { [key: string]: IZoneObject } | {}, visiblity: boolean) => {
    for (const i of Object.keys(zones)) {
      zones[i].visible = visiblity
    }
    return zones
  }

  public render() {
    const canAccess = canAccessByAuthToken()
    const styles = {
      CirclePreLoader: {
        display: 'flex',
        paddingTop: '5px',
        flex: 1,
        justifyContent: 'center',
        alignment: 'center',
        width: '100%'
      }
    }

    if (!canAccess) {
      window.location.replace('/auth')
      return null
    }

    const { tripFilters, tripsPerService } = this.props

    const list: any[] = []
    for (const i of Object.keys(this.props.zones || {})) {
      const zone = this.props.zones[i]
      list.push(zone)
    }

    return (
      <section className='MainMenu mw196 invisible-scrollbar'>
        <Menu
          mode='horizontal'
          className={`topmenu-header ${this.props.isAdmin ? 'menu-white-background' : 'menu-green-background'}`}
          openAnimation='slide-up'
          triggerSubMenuAction='click'
        >
          {this.renderMenuItems()}
        </Menu>
        <div className='MenuItems mt1 leftMenu-list'>
          <div
            className={`MainMenu__MenuItem pt1 leftMenu-list__container topmenu-header ${
              this.props.isAdmin ? 'admin-menu' : 'green-menu'
            }`}
            title='Trips'
          >
            {this.props.isAdmin && (
              <div className='flex items-center pl2 cursor--pointer'>
                <div className='flex-auto'>
                  <Link to='/trips'>
                    <p className='mt0 mb0 bold'>Requests</p>
                  </Link>
                </div>
                {!tripFilters.serviceId && (
                  <div style={{ marginLeft: 'auto' }} className='pr2'>
                    <div className='flex' style={{ width: '100%', height: '100%' }}>
                      <div className='flex' style={{ margin: 'auto' }}>
                        {this.props.visible && (
                          <div style={{ cursor: 'pointer' }} onClick={this.toggleVisible}>
                            <EyeOpen style={{ margin: 'auto' }} className='mr1 block' />
                          </div>
                        )}
                        {!this.props.visible && (
                          <div style={{ cursor: 'pointer' }} onClick={this.toggleVisible}>
                            <EyeClose style={{ margin: 'auto' }} className='mr1 block' />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            {!this.props.isAdmin && (
              <>
                <div className='flex items-start pl2 cursor--pointer leftMenu-list__item'>
                  <div className='mr1 leftMenu-list__item--icon'>
                    <Trip />
                  </div>
                  <div className='flex-auto leftMenu-list__item--text'>
                    <Link to='/station-user-trips'>
                      <p className='mt0 mb0'>Trips</p>
                    </Link>
                  </div>
                </div>
              </>
            )}
            {this.props.isAdmin && !this.state.isFetching && this.props.services.length !== 0 ? (
              <ul className='list-reset mt1'>
                {this.props.services
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(s => {
                    // Disable existing trip indicator
                    // const { totalTrips, indicatorColor, showWarning } = this.getTripIndicatorByServiceId(s.id)
                    const { totalBookedTrips, scheduledTrips } = this.getTripCountPerService(s.id)
                    const tempVehicleCount = this.getTempVehicleCount(s.id)

                    return (
                      <li className={classNames('pl1 mb1', { 'bg-white': tripFilters.serviceId === s.id })} key={s.id}>
                        {!s.hideService && (
                          <div className='cursor--pointer'>
                            <div className='flex justify-between items-center'>
                              <div className='flex'>
                                <Checkbox
                                  aria-label={`Select service: ${s.name}`}
                                  // defaultChecked={true}
                                  checked={this.state.serviceIds.includes(s.id)}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    this.clickServiceLink(e.target.checked, s.id)
                                  }
                                />
                                <button
                                  type='button'
                                  className='btn-transparent left-align'
                                  // onClick={this.clickServiceLink(s.id)}
                                >
                                  <span className='font-dark-grey'>{s.internalName ? s.internalName : s.name}</span>
                                </button>
                              </div>
                              <div className='pl1 pr1'>
                                {/* Disable existing trip indicator */}
                                {/* {totalTrips > 0 && showWarning && (
                                  <div className='indicator-pull-left indicator-warning'>
                                    <ExclamationCircle />
                                  </div>
                                )}
                                {totalTrips > 0 && (
                                  <div className={showWarning ? 'indicator-pull-right' : ''}>
                                    <button
                                      type='button'
                                      className='btn-transparent'
                                      // onClick={this.clickServiceLink(s.id)}
                                    >
                                      <span
                                        className={`font-white pl1 pr1 align-right mr1 border-${indicatorColor} bg-${indicatorColor} rounded`}
                                        style={{ borderRadius: '30%', height: '0.8em', fontSize: '0.75em' }}
                                      >
                                        {totalTrips}
                                      </span>
                                    </button>
                                  </div>
                                )} */}
                                {tempVehicleCount > 0 && (
                                  <span
                                    className={`font-white pl1 pr1 align-right mr1 border-blue bg-blue rounded`}
                                    style={{ borderRadius: '30%', height: '0.8em', fontSize: '0.75em' }}
                                    title='The number of unassigned trips with temporary vehicle'
                                  >
                                    {tempVehicleCount}
                                  </span>
                                )}
                                {!!(totalBookedTrips && totalBookedTrips !== 0) && (
                                  <div className='pill-container border-radius flex items-center'>
                                    <div
                                      className={`pill left-pill fs-10 ${
                                        scheduledTrips !== 0
                                          ? 'bg-light-orange1 font-white'
                                          : 'bg-grey6 font-light-grey'
                                      }`}
                                    >
                                      {scheduledTrips}
                                    </div>
                                    <div className='pill right-pill fs-10 bg-grey4 font-black'>{totalBookedTrips}</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {tripFilters.serviceId === s.id && (
                          <div>
                            {Object.keys(list).map((item, i) => (
                              <ZonesListItem key={i} setZoneVisible={this.setZoneVisible} zone={list[item]} />
                            ))}
                          </div>
                        )}
                      </li>
                    )
                  })}
              </ul>
            ) : (
              <>
                <div style={styles.CirclePreLoader}>
                  <PreloaderCircle loading={true} />
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    )
  }

  public renderMenuItems = () => {
    if (this.props.isAdmin) {
      return this.renderAdminItems()
    }
    return this.renderOrgItems()
  }

  public renderAdminItems = () => {
    const { resourcePermissions } = this.props
    return (
      <SubMenu title={<MenuTitle {...this.props} />} popupOffset={[10, 0]} overflowedIndicator={false} key='1'>
        {resourcePermissions.organization_info !== false && (
          <MenuItem key='1-1'>
            <Link to={`/organizations`}>Organization Info</Link>
          </MenuItem>
        )}
        {resourcePermissions.team_members !== false && (
          <MenuItem key='1-2'>
            <Link to='/team-members'>Team Members</Link>
          </MenuItem>
        )}
        {resourcePermissions.station_user !== false && (
          <MenuItem key='1-3'>
            <Link to='/station-user'>Station User</Link>
          </MenuItem>
        )}
        {resourcePermissions.services !== false && (
          <MenuItem key='1-4'>
            <Link to='/services'>Services</Link>
          </MenuItem>
        )}
        {resourcePermissions.riders !== false && (
          <MenuItem key='1-5'>
            <Link to='/riders'>Riders</Link>
          </MenuItem>
        )}
        {resourcePermissions.products !== false && (
          <MenuItem key='1-6'>
            <Link to='/products'>Products</Link>
          </MenuItem>
        )}
        {resourcePermissions.drivers !== false && (
          <MenuItem key='1-7'>
            <Link to='/drivers'>Drivers</Link>
          </MenuItem>
        )}
        {resourcePermissions.vehicles !== false && (
          <MenuItem key='1-8'>
            <Link to='/vehicles'>Vehicles</Link>
          </MenuItem>
        )}
        {resourcePermissions.trips_management !== false && (
          <MenuItem key='1-9'>
            <Link to='/trips-management'>Trips Management</Link>
          </MenuItem>
        )}
        {resourcePermissions.trips_management !== false && (
          <MenuItem key='1-10'>
            <Link to='/billing-management'>Billing Management</Link>
          </MenuItem>
        )}
        {resourcePermissions.global_settings !== false && this.isOrganizationAdmin() && (
          <MenuItem key='1-11'>
            <Link to='/global-settings'>Global Settings</Link>
          </MenuItem>
        )}
        {resourcePermissions.promotions !== false && (
          <MenuItem key='1-12'>
            <Link to='/promotions'>Promotions</Link>
          </MenuItem>
        )}
        {resourcePermissions.rider_driver_feedbacks !== false && (
          <MenuItem key='1-13'>
            <Link to='/trip-feedbacks'>Rider/Driver Feedback</Link>
          </MenuItem>
        )}
        {resourcePermissions.import !== false && (
          <MenuItem key='1-14'>
            <Link to='/import'>Import</Link>
          </MenuItem>
        )}
        <MenuItem key='1-15'>
          <Link to='/driver-manifests'>Driver Manifests</Link>
        </MenuItem>

        <Divider />
        <MenuItem key='1-16'>
          <p className='m0 p0 cursor--pointer' onClick={signOut}>
            Sign out
          </p>
        </MenuItem>
      </SubMenu>
    )
  }

  public renderOrgItems = () => {
    return (
      <SubMenu title={<MenuTitle {...this.props} />} popupOffset={[10, 0]} key='1'>
        <MenuItem key='1-11'>
          <p className='m0 p0 cursor--pointer' onClick={signOut}>
            Sign out
          </p>
        </MenuItem>
      </SubMenu>
    )
  }
  public isInTripsRoute() {
    return this.props.location.pathname.startsWith('/trips') && !this.props.location.pathname.startsWith('/trips-')
  }
  public isInSchedulerRoute() {
    return this.props.location.pathname.startsWith('/scheduler')
  }

  private isOrganizationAdmin() {
    return checkIfAdmin()
  }

  private setZoneVisible = (zone: IGoinZoneObject, visible: boolean) => {
    const zones = Object.assign({}, this.props.zones)
    zones[zone.id].visible = visible
    if (this.props.toggleZone) {
      this.props.toggleZone(zones)
    }
  }

  private clickServiceLink = (checked: boolean, serviceId: string) => {
    const serviceIds = !checked
      ? this.state.serviceIds.filter(id => id !== serviceId)
      : [...this.state.serviceIds, serviceId]
    this.setState({ isFetching: true, serviceIds })
    this.props
      .updateTripFilters({ serviceIds })
      .then(res => {
        if (serviceId) {
          this.props.fetchService(serviceId)
          if (this.isInSchedulerRoute()) {
            this.fetchGoinZones()
          }
        }
        this.setState({ isFetching: false })
      })
      .catch(error => {
        this.setState({ isFetching: false })
        console.log(error)
      })

    // this will replace the /trips/:tripId on selecting a service from mainMenu
    if (this.isInTripsRoute()) {
      history.push('/trips')
    }
    if (this.props.orgId) {
      this.displayTotalTripsPerService(this.props.orgId, this.newTripStatusIds)
      this.props.getTempVehicleByServiceCount()
    }
  }

  private getTripCountPerService = (serviceId: string) => {
    const { tripsPerService = [] } = this.props
    const serviceTripCount =
      tripsPerService.length > 0 ? this.props.tripsPerService.filter(({ service_id }) => service_id === serviceId) : []
    if (serviceTripCount.length > 0) {
      const { total_booked_trips: totalBookedTrips, trip_statuses } = serviceTripCount[0]
      const scheduledTrips =
        trip_statuses.length > 0
          ? trip_statuses.filter(({ last_status }) => last_status === TripStatuses.SCHEDULE_REQUESTED).length
          : 0
      return { totalBookedTrips, scheduledTrips }
    }
    return { totalBookedTrips: 0, scheduledTrips: 0 }
  }

  private getTripIndicatorByServiceId = (serviceId: string) => {
    const tripResponse = this.props.tripsPerService
      ? Object.values(this.props.tripsPerService).find(item => {
          return item.service_id === serviceId
        })
      : undefined

    const hasWaitingAndNoResponse = (statuses: string[]): boolean => {
      return statuses.some(
        s =>
          [TripStatuses.WAITING, TripStatuses.NO_RESPONSE_OPERATOR, TripStatuses.DISPATCH_ASSIGNED_DRIVER].indexOf(s) >=
          0
      )
    }

    const hasScheduledRequested = (statuses: string[]): boolean =>
      statuses.some(s => [TripStatuses.REQUESTED, TripStatuses.SCHEDULE_REQUESTED].indexOf(s) >= 0)

    let totalTrips = 0
    let indicatorColor = ''
    let showWarning = false

    if (tripResponse) {
      const { total_trips, trip_statuses } = tripResponse
      const lastStatuses = trip_statuses
        .map(({ last_status }) => last_status)
        .filter((value, index, self) => self.indexOf(value) === index)

      // if there are waiting , no response and scheduled requested statuses
      if (hasWaitingAndNoResponse(lastStatuses) && hasScheduledRequested(lastStatuses)) {
        indicatorColor = 'red'
        showWarning = true
      } else if (hasWaitingAndNoResponse(lastStatuses)) {
        // waiting and no response only
        indicatorColor = 'red'
      } else if (hasScheduledRequested(lastStatuses)) {
        // scheduled request statuses only
        indicatorColor = 'light-orange'
      } else {
        indicatorColor = 'red'
      }

      totalTrips = parseInt(total_trips, 10)
    }

    return { totalTrips, indicatorColor, showWarning }
  }

  private getTempVehicleCount = (serviceId: string) => {
    const serviceData = this.props.tempVehicleByService.find(item => item.serviceid === serviceId)
    return serviceData ? serviceData.total : 0
  }
}

export default MainMenu



// WEBPACK FOOTER //
// ./src/components/navigation/MainMenu.tsx